import React, { useState, useEffect, ChangeEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  isPhoneInDatabase,
  register,
  verifyPhone,
  login,
  requestPasswordReset,
  resetPassword,
  resendCode,
} from "../../services/authService";
import { mergeGuestFavorites } from "../../services/favoritesService";
import PasswordInput from "../input/PasswordInput";
import { useUserStore } from "../../store/userStore";

type Step = "phone" | "password" | "verifyCode" | "createPassword" | "register";

function LoginForm() {
  const [step, setStep] = useState<Step>("phone");
  const [phone, setPhone] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [verificationCode, setVerificationCode] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [isCodeResendDisabled, setIsCodeResendDisabled] = useState<boolean>(false);
  const [resendTimer, setResendTimer] = useState<number>(30);
  // Один и тот же экран кода используется и при регистрации, и при
  // восстановлении пароля — дальше шаги разные, поэтому помним причину.
  const [codePurpose, setCodePurpose] = useState<"register" | "reset">("register");

  const navigate = useNavigate();
  const location = useLocation();
  // Куда вернуть после входа: страница, с которой пользователя выбросило.
  const redirectTo = (location.state as { from?: string } | null)?.from || "/profile";

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token && useUserStore.getState().user) {
      navigate(redirectTo, { replace: true });
    }
  }, [navigate, redirectTo]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isCodeResendDisabled) {
      timer = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setIsCodeResendDisabled(false);
            return 30;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isCodeResendDisabled]);

  const formatPhoneNumber = (value: string) => {
    value = value.replace(/[^\d+]/g, "");
    if (value.startsWith("8")) {
      value = "+7" + value.slice(1);
    }
    if (!value.startsWith("+7")) {
      value = "+7" + value.replace(/^\+/, "");
    }
    const formatted = value.replace(/(\+7)(\d{0,3})(\d{0,3})(\d{0,2})(\d{0,2})/, (match, p1, p2, p3, p4, p5) => {
      let result = p1;
      if (p2) result += ` (${p2}`;
      if (p3) result += `) ${p3}`;
      if (p4) result += `-${p4}`;
      if (p5) result += `-${p5}`;
      return result;
    });
    return formatted;
  };

  const handlePhoneChange = (e: ChangeEvent<HTMLInputElement>) => {
    const rawPhone = e.target.value;
    const formattedPhone = formatPhoneNumber(rawPhone);
    setPhone(formattedPhone);
  };

  const handlePhoneFocus = () => {
    if (!phone.startsWith("+7")) {
      setPhone("+7 ");
    }
  };

  const handleSendCode = async () => {
    try {
      setError(null);
      const digitsOnlyPhone = phone.replace(/[^\d]/g, "").slice(1);
      if (digitsOnlyPhone.length !== 10) {
        setError("Введите корректный номер телефона (10 цифр после +7)");
        return;
      }
      const isRegistered = await isPhoneInDatabase(digitsOnlyPhone);
      if (isRegistered) {
        setStep("password");
      } else {
        setStep("register");
      }
    } catch (error) {
      setError("Ошибка проверки номера телефона");
    }
  };

  // Общий финал входа: кладём пользователя в стор, переносим избранное,
  // набранное до авторизации, и возвращаем туда, откуда пришли.
  const completeLogin = async (digitsOnlyPhone: string, userPassword: string) => {
    const response = await login({ phone: digitsOnlyPhone, password: userPassword });
    const { token, user } = response;
    useUserStore.getState().setUser({
      name: user.name,
      phone: user.phone,
      isAdmin: user.is_admin,
      isSuperAdmin: user.is_super_admin,
      token,
    });

    await mergeGuestFavorites();
    navigate(redirectTo, { replace: true });
  };

  const handleRegister = async () => {
    if (!name || password.length < 6 || password.includes(" ")) {
      setError("Введите имя и корректный пароль (минимум 6 символов без пробелов)");
      return;
    }
    try {
      setError(null);
      const digitsOnlyPhone = phone.replace(/[^\d]/g, "").slice(1);
      await register({ name, phone: digitsOnlyPhone, password });
      setCodePurpose("register");
      setStep("verifyCode");
      setIsCodeResendDisabled(true);
    } catch (error) {
      setError("Ошибка регистрации");
    }
  };

  const handleVerifyCode = async () => {
    try {
      setError(null);
      const digitsOnlyPhone = phone.replace(/[^\d]/g, "").slice(1);
      await verifyPhone({ phone: digitsOnlyPhone, code: verificationCode });

      // Восстановление пароля — единственный случай, когда пароль надо
      // придумать: при регистрации он уже задан и сохранён на бэкенде.
      if (codePurpose === "reset") {
        setStep("createPassword");
        return;
      }

      try {
        await completeLogin(digitsOnlyPhone, password);
      } catch (loginError) {
        // Номер подтверждён, но автоматический вход не удался — не бросаем
        // пользователя на пустом экране, а просим просто ввести пароль.
        setStep("password");
        setError("Номер подтверждён. Войдите, пожалуйста, с вашим паролем");
      }
    } catch (error) {
      setError("Неверный код подтверждения");
    }
  };

  const handleCreatePassword = async () => {
    if (newPassword.length < 6 || newPassword.includes(" ")) {
      setError("Пароль должен содержать минимум 6 символов и не должен включать пробелы");
      return;
    }
    try {
      setError(null);
      const digitsOnlyPhone = phone.replace(/[^\d]/g, "").slice(1);
      await resetPassword({ phone: digitsOnlyPhone, code: verificationCode, password: newPassword });
      try {
        await completeLogin(digitsOnlyPhone, newPassword);
      } catch (loginError) {
        setStep("password");
        setError("Пароль обновлён. Войдите, пожалуйста, с новым паролем");
      }
    } catch (error) {
      setError("Ошибка создания пароля");
    }
  };

  const handleLogin = async () => {
    try {
      setError(null);
      const digitsOnlyPhone = phone.replace(/[^\d]/g, "").slice(1);
      await completeLogin(digitsOnlyPhone, password);
    } catch (error) {
      setError("Неверный номер телефона или пароль");
    }
  };

  const handleResendCode = async () => {
    try {
      setError(null);
      const digitsOnlyPhone = phone.replace(/[^\d]/g, "").slice(1);
      await resendCode({ phone: digitsOnlyPhone });
      setIsCodeResendDisabled(true);
    } catch (error) {
      setError("Ошибка отправки кода");
    }
  };

  const handleResetPassword = async () => {
    try {
      setError(null);
      const digitsOnlyPhone = phone.replace(/[^\d]/g, "").slice(1);
      await requestPasswordReset({ phone: digitsOnlyPhone });
      setCodePurpose("reset");
      setStep("verifyCode");
      setIsCodeResendDisabled(true);
    } catch (error) {
      setError("Ошибка сброса пароля");
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Enter") {
        switch (step) {
          case "phone":
            handleSendCode();
            break;
          case "register":
            handleRegister();
            break;
          case "password":
            handleLogin();
            break;
          case "verifyCode":
            handleVerifyCode();
            break;
          case "createPassword":
            handleCreatePassword();
            break;
          default:
            break;
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [step, phone, password, verificationCode, newPassword, name]);

  return (
    <div className="inset-0 flex items-center justify-center p-4 md:p-20">
      <div className="bg-[#F2F2F2] px-6 md:px-20 py-12 rounded-lg w-full max-w-lg">
        {step === "phone" && (
          <div>
            <h2 className="text-xl font-semibold mb-4">Авторизация / Регистрация</h2>
            <input
              type="text"
              value={phone}
              onChange={handlePhoneChange}
              onFocus={handlePhoneFocus}
              placeholder="Номер телефона"
              maxLength={18}
              className="w-full border rounded-lg px-4 py-2 mb-4 focus:outline-none focus:ring-[#008EFF] focus:border-[#008EFF]"
            />
            <div className="text-xs text-gray-500 mb-4">
              Нажимая кнопку «Далее», вы даете согласие на{" "}
              <a
                href="/data-processing"
                className="text-[#008EFF] hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Сбор и обработку персональных данных
              </a>
              ,{" "}
              <a
                href="/privacy-policy"
                className="text-[#008EFF] hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Политику конфиденциальности
              </a>{" "}
              и соглашаетесь с{" "}
              <a
                href="/user-agreement"
                className="text-[#008EFF] hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Условиями пользовательского соглашения
              </a>
            </div>

            <button
              onClick={handleSendCode}
              className={`w-full ${phone.length < 18 ? "bg-[#D9D9D9]" : "bg-black"} text-white py-2 rounded-lg hover:bg-stone-800 transition`}
              disabled={phone.length < 18}
            >
              Далее
            </button>
          </div>
        )}

        {step === "register" && (
          <div>
            <h2 className="text-xl font-semibold mb-4">Регистрация</h2>
            <input
              type="text"
              value={phone}
              onChange={handlePhoneChange}
              onFocus={handlePhoneFocus}
              placeholder="Номер телефона"
              className="w-full border rounded-lg px-4 py-2 mb-4 focus:outline-none focus:ring-[#008EFF] focus:border-[#008EFF]"
            />
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Имя"
              className="w-full border rounded-lg px-4 py-2 mb-4 focus:outline-none focus:ring-[#008EFF] focus:border-[#008EFF]"
            />
            <PasswordInput password={password} setPassword={setPassword} />
            <button
              onClick={handleRegister}
              className={`w-full ${password.length < 6 || !name ? "bg-[#D9D9D9]" : "bg-black"}  text-white py-2 rounded-lg hover:bg-stone-800 transition`}
              disabled={password.length < 6 || !name}
            >
              Зарегистрироваться
            </button>
          </div>
        )}

        {step === "password" && (
          <div>
            <h2 className="text-xl font-semibold mb-4">Введите пароль</h2>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Пароль"
              className="w-full border rounded-lg px-4 py-2 mb-4 focus:outline-none focus:ring-[#008EFF] focus:border-[#008EFF]"
            />
            <button
              onClick={handleLogin}
              className={`w-full ${password.length < 6 ? "bg-[#D9D9D9]" : "bg-black"}  text-white py-2 rounded-lg hover:bg-stone-800 transition mb-2`}
              disabled={password.length < 6}
            >
              Войти
            </button>
            <button
              onClick={() => handleResetPassword()}
              className="w-full bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 transition"
            >
              Сбросить пароль
            </button>
          </div>
        )}

        {step === "verifyCode" && (
          <div>
            <h2 className="text-xl font-semibold mb-4">Код из WhatsApp</h2>
            <p className="text-sm text-gray-500 mb-4">
              Мы отправили код в WhatsApp на номер {phone}
            </p>
            <input
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              placeholder="Введите код"
              className="w-full border rounded-lg px-4 py-2 mb-4 focus:outline-none focus:ring-[#008EFF] focus:border-[#008EFF]"
            />
            <button
              onClick={handleVerifyCode}
              className={`w-full ${phone.length < 18 ? "bg-[#D9D9D9]" : "bg-black"}  text-white py-2 rounded-lg hover:bg-stone-800 transition mb-2`}
            >
              Подтвердить
            </button>
            <button
              disabled={isCodeResendDisabled}
              onClick={handleResendCode}
              className={`w-full py-2 rounded-lg transition ${isCodeResendDisabled ? "bg-gray-300 text-gray-500" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
            >
              {isCodeResendDisabled ? `Повторить через ${resendTimer}с` : "Отправить код снова"}
            </button>
          </div>
        )}

        {step === "createPassword" && (
          <div>
            <h2 className="text-xl font-semibold mb-4">Создайте пароль</h2>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Новый пароль"
              className="w-full border rounded-lg px-4 py-2 mb-4 focus:outline-none focus:ring-[#008EFF] focus:border-[#008EFF]"
            />
            <button
              onClick={handleCreatePassword}
              className={`w-full ${phone.length < 18 ? "bg-[#D9D9D9]" : "bg-black"}  text-white py-2 rounded-lg hover:bg-stone-800 transition`}
            >
              Создать
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default LoginForm;